/**
 * Changelog (Novedades) Page
 *
 * Renders CHANGELOG.md as a timeline of releases with colored badges per section.
 *
 * @module pages/Changelog
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Container, Card, Badge, Alert, Spinner } from 'react-bootstrap';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import { APP_VERSION, APP_NAME } from '../utils/appVersion';
import { formatDate } from '../utils/formatters';
import DashboardLayout from '../components/layout/DashboardLayout';
import './Changelog.css';

// Maps changelog section header -> Bootstrap badge variant + i18n key
const SECTION_META = {
  Added:    { variant: 'success', icon: 'bi-plus-circle',    i18nKey: 'changelog.sections.added' },
  Fixed:    { variant: 'primary', icon: 'bi-wrench',         i18nKey: 'changelog.sections.fixed' },
  Changed:  { variant: 'warning', icon: 'bi-arrow-repeat',   i18nKey: 'changelog.sections.changed' },
  Removed:  { variant: 'danger',  icon: 'bi-dash-circle',    i18nKey: 'changelog.sections.removed' },
  Security: { variant: 'dark',    icon: 'bi-shield-lock',    i18nKey: 'changelog.sections.security' },
  Deprecated:{ variant: 'secondary', icon: 'bi-hourglass-split', i18nKey: 'changelog.sections.deprecated' },
};

/**
 * Parse CHANGELOG.md text into structured releases.
 * Expects "Keep a Changelog" format:
 *   ## [x.y.z] - YYYY-MM-DD
 *   ### Added|Fixed|Changed|Removed
 *   - bullet
 */
export function parseChangelog(text) {
  if (!text || typeof text !== 'string') return [];
  const lines = text.split(/\r?\n/);

  const releases = [];
  let current = null;
  let section = null;
  let sectionBuffer = [];

  const flushSection = () => {
    if (current && section && sectionBuffer.length > 0) {
      const markdown = sectionBuffer.join('\n').trim();
      if (markdown) {
        current.sections.push({ type: section, markdown });
      }
    }
    section = null;
    sectionBuffer = [];
  };

  const flushRelease = () => {
    flushSection();
    if (current) releases.push(current);
    current = null;
  };

  for (const line of lines) {
    const releaseMatch = line.match(/^##\s+\[([^\]]+)\](?:\s+-\s+(\S+))?/);
    if (releaseMatch) {
      flushRelease();
      const version = releaseMatch[1];
      const date = releaseMatch[2] || null;
      if (version.toLowerCase() === 'unreleased') {
        // skip Unreleased placeholder block
        current = null;
        continue;
      }
      current = { version, date, sections: [] };
      continue;
    }

    if (!current) continue;

    const sectionMatch = line.match(/^###\s+(\w+)/);
    if (sectionMatch) {
      flushSection();
      section = sectionMatch[1];
      continue;
    }

    if (section) {
      sectionBuffer.push(line);
    } else if (line.trim() && current.sections.length === 0 && !current.summary) {
      // Free-form summary text between release header and first subsection
      current.summary = (current.summary || '') + (current.summary ? '\n' : '') + line;
    } else if (current.summary && line.trim()) {
      current.summary += '\n' + line;
    }
  }

  flushRelease();
  return releases;
}

const Changelog = () => {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [raw, setRaw] = useState('');

  useEffect(() => {
    let cancelled = false;
    const url = `${process.env.PUBLIC_URL || ''}/CHANGELOG.md`;
    fetch(url, { cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.text();
      })
      .then((text) => {
        if (!cancelled) {
          setRaw(text);
          setStatus('ready');
        }
      })
      .catch((err) => {
        console.error('[Changelog] failed to load', err);
        if (!cancelled) setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const releases = useMemo(() => parseChangelog(raw), [raw]);

  const locale = (i18n?.language || 'es').startsWith('en') ? 'en-US' : 'es-CR';

  return (
    <DashboardLayout>
      <div className="changelog-page">
      <Container className="py-2">
        <div className="changelog-header">
          <div className="changelog-header-text">
            <h1 className="changelog-title">
              <i className="bi bi-stars me-2" aria-hidden="true"></i>
              {t('changelog.title', 'Novedades')}
            </h1>
            <p className="changelog-subtitle">
              {t('changelog.subtitle', 'Historial de cambios de la plataforma.')}
            </p>
          </div>
          <div className="changelog-current-chip">
            <span className="changelog-current-chip-label">
              {t('changelog.currentlyRunning', 'Estás usando')}
            </span>
            <span className="changelog-current-chip-version">
              {APP_NAME} v{APP_VERSION}
            </span>
          </div>
        </div>

        {status === 'loading' && (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" variant="primary">
              <span className="visually-hidden">{t('common.loading', 'Cargando...')}</span>
            </Spinner>
          </div>
        )}

        {status === 'error' && (
          <Alert variant="warning" className="mt-3">
            <Alert.Heading>
              <i className="bi bi-exclamation-triangle me-2" aria-hidden="true"></i>
              {t('changelog.loadingErrorTitle', 'No pudimos cargar el historial')}
            </Alert.Heading>
            <p className="mb-0">
              {t('changelog.loadingError', 'Intenta recargar la página. Si el problema persiste, contacta al administrador.')}
            </p>
          </Alert>
        )}

        {status === 'ready' && releases.length === 0 && (
          <Alert variant="info" className="mt-3">
            {t('changelog.empty', 'No hay releases publicados todavía.')}
          </Alert>
        )}

        {status === 'ready' && releases.length > 0 && (
          <div className="changelog-timeline">
            {releases.map((release, idx) => {
              const isCurrent = release.version === APP_VERSION;
              return (
                <Card key={`${release.version}-${idx}`} className={`changelog-card ${isCurrent ? 'is-current' : ''}`}>
                  <div className="changelog-card-marker" aria-hidden="true">
                    <div className="changelog-card-dot" />
                  </div>
                  <Card.Body>
                    <div className="changelog-card-header">
                      <div>
                        <h2 className="changelog-version">v{release.version}</h2>
                        {release.date && (
                          <div className="changelog-date text-muted">
                            <i className="bi bi-calendar3 me-2" aria-hidden="true"></i>
                            {formatDate(release.date, locale)}
                          </div>
                        )}
                      </div>
                      {isCurrent && (
                        <Badge bg="primary" className="changelog-current-badge">
                          <i className="bi bi-circle-fill me-1" aria-hidden="true" style={{ fontSize: '0.5rem' }}></i>
                          {t('changelog.currentVersionBadge', 'Versión actual')}
                        </Badge>
                      )}
                    </div>

                    {release.summary && (
                      <div className="changelog-summary">
                        <ReactMarkdown>{release.summary}</ReactMarkdown>
                      </div>
                    )}

                    {release.sections.length > 0 ? (
                      <div className="changelog-sections">
                        {release.sections.map((sec, i) => {
                          const meta = SECTION_META[sec.type] || { variant: 'secondary', icon: 'bi-dot', i18nKey: null };
                          const label = meta.i18nKey ? t(meta.i18nKey, sec.type) : sec.type;
                          return (
                            <div key={`${sec.type}-${i}`} className="changelog-section">
                              <Badge bg={meta.variant} className="changelog-section-badge">
                                <i className={`bi ${meta.icon} me-1`} aria-hidden="true"></i>
                                {label}
                              </Badge>
                              <div className="changelog-section-body">
                                <ReactMarkdown>{sec.markdown}</ReactMarkdown>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      !release.summary && (
                        <p className="text-muted mb-0 fst-italic">
                          {t('changelog.noDetails', 'Sin detalles documentados.')}
                        </p>
                      )
                    )}
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        )}
      </Container>
      </div>
    </DashboardLayout>
  );
};

export default Changelog;
